import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, ListChecks, Plus, Trash2, Users } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import type { WorkingHours } from '@aeris/contracts'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  SwitchField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { WorkingHoursEditor } from '../components/working-hours-editor.tsx'
import {
  addQueueMember,
  ApiError,
  createQueue,
  fetchQueues,
  fetchSalespeople,
  removeQueueMember,
  updateQueue,
  type QueueSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Atendimento → Filas: CRUD de filas, horário, robô-primeiro e membros
 * (F047; página em vez de modal, e horário construído em vez de JSON — F058).
 *
 * A F046 deu ao inbox `assignee_employee_id`, e ele ficava sempre nulo — não
 * havia fila nem atendente cadastrado para preencher. Esta tela é o
 * cadastro: quem atende (`communication_queue_member`, um
 * `cashier`/`salesperson` que ganha uma linha aqui — nenhum papel novo) e
 * quando (`working_hours`, `bot_first`) uma fila responde. O ROBÔ em si é a
 * F052; o que existe aqui é só a chave que ele vai ler.
 *
 * **Página, não modal — motivo concreto.** Até a F058, o formulário vivia
 * num `Dialog`, e a seção de membros era renderizada DEPOIS do `</form>` e
 * do `DialogFooter` sticky — os botões Cancelar/Salvar apareciam no MEIO do
 * diálogo, com a lista de membros abaixo deles. `router.tsx` está sendo
 * editado em paralelo a esta feature (fora do escopo tocar nele agora), então
 * em vez de uma sub-rota `/atendimento-filas/nova`, o formulário troca de
 * VISÃO dentro da MESMA rota — ainda uma página cheia, sem `Dialog` nenhum,
 * o que já resolve o defeito concreto (nada mais é renderizado depois de um
 * rodapé sticky, porque não há rodapé sticky).
 */

const formSchema = z.object({
  name: z.string().trim().min(1, 'Informe o nome').max(120),
  description: z.string().trim().max(1000),
})

type FormValues = z.infer<typeof formSchema>

type QueueFormViewProps = {
  queue?: QueueSummaryType | undefined
  onCancel: () => void
  onSaved: () => Promise<void> | void
}

/** Cria ou edita uma fila, como página cheia. Membros só aparecem ao editar — uma fila nova não tem id para associar ninguém ainda. */
const QueueFormView = ({
  queue,
  onCancel,
  onSaved,
}: QueueFormViewProps): ReactNode => {
  const editing = queue !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [memberError, setMemberError] = useState<string | null>(null)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [removing, setRemoving] = useState<{
    employeeId: string
    name: string
  } | null>(null)

  // Campos que não são `<input>`s nativos vivem fora do react-hook-form —
  // `watch()` quebraria o React Compiler (CLAUDE.md); o mesmo caminho de
  // `atendimento-robo.tsx`.
  const [active, setActive] = useState(queue?.active ?? true)
  const [botFirst, setBotFirst] = useState(queue?.botFirst ?? false)
  const [workingHours, setWorkingHours] = useState<WorkingHours | null>(
    queue?.workingHours ?? null
  )

  const salespeople = useQuery({
    queryKey: ['team-salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
    enabled: editing,
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: queue?.name ?? '',
      description: queue?.description ?? '',
    },
  })

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing) {
        await updateQueue(queue.id, {
          name: values.name.trim(),
          description:
            values.description.trim() === '' ? null : values.description,
          active,
          botFirst,
          workingHours,
          version: queue.version,
        })
        return
      }

      await createQueue({
        name: values.name.trim(),
        description:
          values.description.trim() === '' ? null : values.description,
        active,
        botFirst,
        workingHours,
      })
    },
    onSuccess: async () => {
      setFormError(null)
      await onSaved()
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar esta fila.'
      )
    },
  })

  const addMember = useMutation({
    mutationFn: async (employeeId: string) => {
      if (!editing) return
      await addQueueMember(queue.id, { employeeId })
    },
    onSuccess: async () => {
      setMemberError(null)
      setSelectedEmployeeId('')
      await queryClient.invalidateQueries({
        queryKey: ['communication-queues'],
      })
    },
    onError: (cause: unknown) => {
      setMemberError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível adicionar este atendente.'
      )
    },
  })

  const removeMember = useMutation({
    mutationFn: async (employeeId: string) => {
      if (!editing) return
      await removeQueueMember(queue.id, employeeId)
    },
    onSuccess: async () => {
      setMemberError(null)
      setRemoving(null)
      await queryClient.invalidateQueries({
        queryKey: ['communication-queues'],
      })
    },
    onError: (cause: unknown) => {
      setMemberError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível remover este atendente.'
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    save.mutate(values)
  })

  const currentMemberIds = new Set(
    (queue?.members ?? []).map((member) => member.employeeId)
  )
  const availableSalespeople = (salespeople.data?.items ?? []).filter(
    (person) => !currentMemberIds.has(person.userId)
  )

  return (
    <div className="flex flex-col gap-5">
      <TabHeader
        title={editing ? `Editar fila — ${queue.name}` : 'Nova fila'}
        description="Nome, descrição, quando a fila atende e quem responde por ela."
        actions={
          <Button type="button" variant="ghost" onClick={onCancel}>
            Voltar para a lista
          </Button>
        }
      />

      <form
        noValidate
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <Section title="Dados">
          <Field label="Nome" required error={errors.name?.message}>
            {(field) => (
              <Input
                {...field}
                {...register('name')}
                autoFocus
                placeholder="Suporte"
              />
            )}
          </Field>

          <Field
            label="Descrição"
            error={errors.description?.message}
            hint="Opcional — para quem administra reconhecer a fila."
          >
            {(field) => (
              <Textarea {...field} {...register('description')} rows={2} />
            )}
          </Field>

          <SwitchField
            label="Fila ativa"
            description="Aparece para atribuição e para assumir conversas."
            checked={active}
            onCheckedChange={setActive}
          />

          <SwitchField
            label="Dentro do horário, o robô fala antes da fila humana"
            checked={botFirst}
            onCheckedChange={setBotFirst}
          />
        </Section>

        <Section
          title="Horário"
          description="Quando esta fila atende. Sem horário próprio, ela segue o horário do robô."
        >
          {queue === undefined || queue.workingHoursLegacy === null ? null : (
            <div className="flex flex-col gap-2 rounded-(--radius-md) bg-(--warning-subtle) p-3">
              <p className="text-(length:--text-sm) text-(--warning)">
                Este horário estava num formato que o robô nunca leu;
                reconfigure abaixo.
              </p>
              <pre className="overflow-x-auto rounded-(--radius-sm) bg-(--surface-sunken) p-2 font-(--font-mono) text-(length:--text-xs) text-(--text-secondary)">
                {JSON.stringify(queue.workingHoursLegacy, null, 2)}
              </pre>
            </div>
          )}

          <WorkingHoursEditor
            value={workingHours}
            onChange={setWorkingHours}
            nullable
            inheritLabel="Esta fila tem um horário próprio (diferente do robô)"
          />
        </Section>

        {formError === null ? null : (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {formError}
          </p>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Salvar
          </Button>
        </div>
      </form>

      {editing ? (
        <Section title="Membros" description="Quem atende por esta fila.">
          {queue.members.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum atendente nesta fila ainda.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {queue.members.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center justify-between gap-2 rounded-(--radius-md) border border-(--border-default) px-2.5 py-1.5 text-(length:--text-sm)"
                >
                  <span>{member.employeeName ?? member.employeeId}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={removeMember.isPending}
                    onClick={() => {
                      setRemoving({
                        employeeId: member.employeeId,
                        name: member.employeeName ?? 'este atendente',
                      })
                    }}
                    aria-label={`Remover ${member.employeeName ?? 'atendente'} da fila`}
                  >
                    <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-end gap-2">
            <Field label="Adicionar atendente" className="flex-1">
              {(field) => (
                <Select
                  {...field}
                  value={selectedEmployeeId}
                  onChange={(event) => {
                    setSelectedEmployeeId(event.target.value)
                  }}
                  disabled={salespeople.isPending}
                >
                  <option value="">Escolha uma pessoa</option>
                  {availableSalespeople.map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button
              type="button"
              variant="secondary"
              disabled={selectedEmployeeId === '' || addMember.isPending}
              onClick={() => {
                addMember.mutate(selectedEmployeeId)
              }}
            >
              Adicionar
            </Button>
          </div>

          {memberError === null ? null : (
            <p role="alert" className="text-(length:--text-xs) text-(--danger)">
              {memberError}
            </p>
          )}
        </Section>
      ) : null}

      {removing === null ? null : (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setRemoving(null)
          }}
          title="Remover atendente da fila?"
          description={`${removing.name} deixa de aparecer para atribuição e assumir conversas nesta fila. A pessoa continua cadastrada na equipe.`}
          confirmLabel="Remover"
          tone="danger"
          loading={removeMember.isPending}
          onConfirm={() => {
            removeMember.mutate(removing.employeeId)
          }}
        />
      )}
    </div>
  )
}

type View = { kind: 'list' } | { kind: 'form'; queueId: string | null }

export const AtendimentoFilasPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canWrite = allows('communication.queue.write')

  const [view, setView] = useState<View>({ kind: 'list' })

  const queues = useQuery({
    queryKey: ['communication-queues'],
    queryFn: async ({ signal }) => await fetchQueues(signal),
  })

  const rows = queues.data ?? []
  const editingQueue =
    view.kind === 'form' && view.queueId !== null
      ? rows.find((row) => row.id === view.queueId)
      : undefined

  return (
    <>
      {view.kind === 'form' ? (
        <QueueFormView
          key={view.queueId ?? 'new'}
          queue={editingQueue}
          onCancel={() => {
            setView({ kind: 'list' })
          }}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['communication-queues'],
            })
            setView({ kind: 'list' })
          }}
        />
      ) : (
        <>
          <TabHeader
            title="Filas de atendimento"
            description="Quem atende, e quando — cada fila tem seus membros e um horário opcional."
            actions={
              canWrite ? (
                <Button
                  onClick={() => {
                    setView({ kind: 'form', queueId: null })
                  }}
                >
                  <Plus aria-hidden="true" /> Nova fila
                </Button>
              ) : null
            }
          />

          {queues.isPending ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : queues.isError ? (
            <EmptyState
              icon={<ListChecks aria-hidden="true" className="h-8 w-8" />}
              title="Não foi possível carregar as filas"
              description="Verifique a conexão com o servidor e tente novamente."
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ListChecks aria-hidden="true" className="h-8 w-8" />}
              title="Nenhuma fila cadastrada"
              description={
                canWrite
                  ? 'Crie uma fila para organizar quem atende no WhatsApp.'
                  : 'Peça a um administrador para cadastrar as filas de atendimento.'
              }
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Fila</TableHeaderCell>
                    <TableHeaderCell>Situação</TableHeaderCell>
                    <TableHeaderCell>Robô primeiro</TableHeaderCell>
                    <TableHeaderCell>Membros</TableHeaderCell>
                    <TableHeaderCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((queue) => (
                    <TableRow key={queue.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-(--weight-medium)">
                            {queue.name}
                          </span>
                          {queue.description === null ? null : (
                            <span className="text-(length:--text-xs) text-(--text-muted)">
                              {queue.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge tone={queue.active ? 'success' : 'neutral'}>
                          {queue.active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {queue.botFirst ? (
                          <Badge
                            tone="info"
                            icon={
                              <Bot aria-hidden="true" className="h-3 w-3" />
                            }
                          >
                            Robô primeiro
                          </Badge>
                        ) : (
                          <span className="text-(length:--text-xs) text-(--text-muted)">
                            Fila humana direto
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {queue.members.length === 0 ? (
                          <span className="text-(length:--text-xs) text-(--text-muted)">
                            Nenhum atendente
                          </span>
                        ) : (
                          <span className="text-(length:--text-sm)">
                            {queue.members
                              .map((member) => member.employeeName ?? '—')
                              .join(', ')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setView({ kind: 'form', queueId: queue.id })
                            }}
                            aria-label={`Editar ${queue.name}`}
                          >
                            <Users aria-hidden="true" className="h-3.5 w-3.5" />
                            Editar
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  )
}
